import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { MapPin, Phone, Mail, Camera, Clock, Wifi, BookOpen, Users, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Campuses() {
  const { t } = useTranslation();

  const gallery = [
    { src: '/images/campus/entrance.jpg', alt: "Entrée principale ESTAD" },
    { src: '/images/campus/building-01.jpg', alt: "Bâtiment ESTAD" },
    { src: '/images/campus/building-02.jpg', alt: "Campus ESTAD" },
    { src: '/images/campus/classroom-01.jpg', alt: "Salle de cours" },
    { src: '/images/campus/classroom-02.jpg', alt: "Amphi ESTAD" },
    { src: '/images/campus/espace-01.jpg', alt: "Espace étudiant" },
    { src: '/images/campus/espace-02.jpg', alt: "Salle commune" },
    { src: '/images/campus/espace-03.jpg', alt: "Hall" },
    { src: '/images/campus/activity-01.jpg', alt: "Activité étudiante" },
    { src: '/images/campus/activity-02.jpg', alt: "Vie de campus" },
    { src: '/images/campus/students-01.jpg', alt: "Étudiants ESTAD" },
    { src: '/images/campus/doc-event-01.jpg', alt: "Événement académique" },
  ];

  const facilities = [
    { icon: <BookOpen className="w-6 h-6" />, title: "Bibliothèque & Centre de Ressources", desc: "Accès à des milliers de références académiques, revues scientifiques et bases de données internationales." },
    { icon: <Wifi className="w-6 h-6" />, title: "Salles Informatiques & Labo CATI", desc: "Équipements de pointe pour la recherche, le développement et les formations pratiques. Centre CATI-OMPI certifié." },
    { icon: <Users className="w-6 h-6" />, title: "Espaces Collaboratifs", desc: "Zones de travail en groupe, salles de réunion et espaces de co-working pour favoriser l'innovation collective." },
    { icon: <Camera className="w-6 h-6" />, title: "Studio Audiovisuel", desc: "Studio équipé pour la production de contenu numérique, les cours en ligne et les projets Arts Numériques." },
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={t('nav_campuses', 'Notre Campus')}
        description="ESTAD est installée au Palais des Congrès de Yaoundé — un site premium, central et accessible, au cœur de la capitale politique du Cameroun."
        breadcrumbs={[{ label: 'Campus', href: '/campuses' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* ── Main Campus Hero ── */}
          <section id="campus-principal" className="space-y-10">

            {/* Large Hero Image */}
            <div className="w-full h-72 md:h-[420px] overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold relative group">
              <img
                src="/images/campus/building-02.jpg"
                alt="Campus ESTAD — Palais des Congrès Yaoundé"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-12">
                <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-gold border border-gold/40 px-3 py-1 rounded-full bg-navy/40 backdrop-blur-sm mb-3">
                  Campus Principal
                </span>
                <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tighter italic leading-tight drop-shadow-lg">
                  Palais des Congrès de Yaoundé
                </h2>
                <p className="text-white/80 font-medium text-sm mt-2">Partie Basse — Yaoundé, Cameroun</p>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-6">
                <h3 className="text-2xl font-black text-navy uppercase tracking-tight italic border-l-8 border-gold pl-4">
                  Informations Pratiques
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-sm group hover:bg-white hover:border hover:border-gold transition-all">
                    <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Adresse</div>
                      <div className="text-navy font-black text-sm">Palais des Congrès, Partie Basse</div>
                      <div className="text-gray-500 text-sm font-medium">Yaoundé, Cameroun</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-sm group hover:bg-white hover:border hover:border-gold transition-all">
                    <Phone className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Téléphone</div>
                      <a href="tel:+237682291212" className="text-navy font-black text-sm hover:text-gold transition-colors">(+237) 682 29 12 12</a>
                      <div className="text-gray-500 text-sm font-medium">(+237) 696 98 48 48 / 690 02 80 61</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-sm group hover:bg-white hover:border hover:border-gold transition-all">
                    <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Email</div>
                      <a href="mailto:secretariat@estad-university.com" className="text-navy font-black text-sm hover:text-gold transition-colors">secretariat@estad-university.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-sm">
                    <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Heures d'Ouverture</div>
                      <div className="text-navy font-black text-sm">Lundi — Vendredi : 8h00 — 17h00</div>
                      <div className="text-gray-500 text-sm font-medium">Samedi (sur rendez-vous)</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-black text-navy uppercase tracking-tight italic border-l-8 border-gold pl-4">
                  Pourquoi ce Campus ?
                </h3>
                <p className="text-gray-600 font-medium leading-relaxed">
                  Le Palais des Congrès de Yaoundé est un lieu de prestige national, facilement accessible depuis toutes les zones de la capitale. Ce cadre exceptionnel reflète la vision d'ESTAD : offrir une éducation de qualité internationale dans un environnement inspirant.
                </p>
                <div className="space-y-3">
                  {[
                    "Situé au cœur de Yaoundé — capital politique du Cameroun",
                    "Accès facile en transport en commun et en voiture",
                    "Sécurité renforcée du site 24h/24",
                    "Environnement professionnel et climatisé",
                    "Parking disponible pour étudiants et personnel"
                  ].map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      {feat}
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="inline-flex items-center gap-3 bg-navy text-white px-8 py-4 font-black uppercase tracking-widest text-xs hover:bg-gold hover:text-navy transition-all active:scale-95 mt-4">
                  Nous Rendre Visite <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>

          {/* ── Installations & Équipements ── */}
          <section id="facilities" className="space-y-10">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <BookOpen className="w-7 h-7 text-gold" /> Installations & Équipements
            </h2>

            {/* Facilities image */}
            <div className="w-full h-52 md:h-72 overflow-hidden rounded-sm shadow-xl border-b-8 border-gold">
              <img src="/images/facilities/facility-01.jpg" alt="Installations ESTAD" className="w-full h-full object-cover" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {facilities.map((fac, idx) => (
                <div key={idx} className="group bg-gray-50 border-l-8 border-navy p-6 rounded-sm hover:bg-white hover:shadow-xl hover:border-gold transition-all duration-300 space-y-3">
                  <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-white transition-all duration-300">
                    {fac.icon}
                  </div>
                  <h4 className="font-black text-navy uppercase tracking-tight text-sm">{fac.title}</h4>
                  <p className="text-gray-500 text-xs font-medium leading-relaxed">{fac.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Vie de Campus & Galerie ── */}
          <section id="gallery" className="space-y-10">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <Camera className="w-7 h-7 text-gold" /> Galerie — Vie de Campus
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {gallery.map((img, idx) => (
                <div key={idx} className="aspect-square overflow-hidden rounded-sm group cursor-pointer shadow-sm hover:shadow-xl transition-all">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* ── Associations Étudiantes & Vie Spirituelle ── */}
          <section id="associations" className="bg-navy p-8 md:p-14 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000 pointer-events-none" />
            <div className="relative z-10 space-y-12">
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tighter italic leading-none">
                <Users className="w-9 h-9 text-gold inline mr-4" />
                Vie Étudiante & Associations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-8 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-all group/card">
                  <h3 className="text-xl font-black text-gold uppercase mb-3 group-hover/card:translate-x-1 transition-transform">BEDIR</h3>
                  <p className="text-white/70 font-medium text-sm leading-relaxed">
                    Bureau des Étudiants pour le Développement et l'Innovation Responsable — Corps étudiant officiel d'ESTAD, organisateur des événements campus et représentant des intérêts des étudiants.
                  </p>
                </div>
                <div className="p-8 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-all group/card">
                  <h3 className="text-xl font-black text-gold uppercase mb-3 group-hover/card:translate-x-1 transition-transform">ANIPES — Centre ESTAD</h3>
                  <p className="text-white/70 font-medium text-sm leading-relaxed">
                    Association Nationale pour l'Inclusion et la Promotion de l'Enseignement Supérieur. ESTAD est le centre ANIPES de Yaoundé 2, dédié à la diversité et à l'inclusion dans l'enseignement supérieur.
                  </p>
                </div>
                <div className="p-8 bg-white/5 border border-white/10 rounded-sm hover:bg-white/10 transition-all group/card md:col-span-2">
                  <h3 className="text-xl font-black text-gold uppercase mb-3">Dimension Sociale & Spirituelle</h3>
                  <p className="text-white/70 font-medium text-sm leading-relaxed">
                    Sœur Révérende Victorine, Emmanuel et Rachel vous accueillent chaleureusement. Notre campus cultive un environnement bienveillant et inclusif, porteur de valeurs humanistes : <em>"Ne rien faire pour nuire, tout faire pour construire la paix."</em>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── Bilinguisme ── */}
          <section id="bilinguisme" className="border-2 border-gray-100 p-8 md:p-12 rounded-sm bg-gray-50 relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-2 h-full bg-gold" />
            <div className="space-y-6">
              <h2 className="text-2xl font-black text-navy uppercase tracking-tight italic">{t('campus_bilingual_title', 'Politique de Bilinguisme')}</h2>
              <p className="text-gray-600 font-medium leading-relaxed text-lg max-w-3xl">
                {t('campus_bilingual_desc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {[t('campus_bilingual_detail1'), t('campus_bilingual_detail2')].map((detail, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-navy font-bold text-sm">
                    <div className="w-2 h-2 rounded-full bg-gold flex-shrink-0" />
                    {detail}
                  </div>
                ))}
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
